import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, Plus, Trash2, Loader2, KeyRound, ShieldCheck, Power, PowerOff,
  AlertTriangle, Check,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
/**
 * Generic sub-admin management page used for BOTH:
 *  - Super Admin → sub_super_admin (basePath="/admin", labels for super)
 *  - School Owner → sub_owner (basePath="/owner", labels for owner)
 *
 * Props:
 *  scope          : "super" | "owner"  (drives copy + endpoint paths)
 *  backLink       : URL of the back-arrow (e.g., "/admin" or "/dashboard")
 *  pageTitle      : "Sub-Super Admins" or "Sub-Owners"
 */
export default function SubAdminsManagementPage({ scope, backLink, pageTitle }) {
  const basePath = scope === "super" ? "/admin/sub-admins" : "/owner/sub-owners";
  const resetBase = scope === "super" ? "/admin/users" : "/owner/staff";

  const [items, setItems] = useState([]);
  const [permList, setPermList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(basePath);
      setItems(data.items || []);
      setPermList(data.available_permissions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  return (
    <>
      <div className="space-y-5" data-testid="subadmins-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase font-heading">{scope === "super" ? "Super admin" : "Owner"}</p>
            <h1 className="font-heading text-3xl font-bold text-foreground">{pageTitle}</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Delegate access to your team with fine-grained permission toggles.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-heading font-semibold shadow-floating h-10 px-5"
            data-testid="create-sub-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add {scope === "super" ? "sub-admin" : "sub-owner"}
          </Button>
        </div>

        <div className="space-y-4">
          {loading && <p className="text-center text-slate-500 py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-1.5" /> Loading…</p>}
          {!loading && items.length === 0 && (
            <Card className="rounded-3xl border-0 shadow-floating p-10 text-center">
              <ShieldCheck className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="font-heading font-bold">No {scope === "super" ? "sub-super-admins" : "sub-owners"} yet</p>
              <p className="text-sm text-slate-500 mt-1">Add a teammate to delegate access with custom permissions.</p>
            </Card>
          )}
          {items.map((u) => (
            <SubAdminRow key={u.id} user={u} permList={permList} basePath={basePath} resetBase={resetBase} onChanged={load} scope={scope} />
          ))}
        </div>

        <CreateSubAdminDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          permList={permList}
          basePath={basePath}
          scope={scope}
          onCreated={load}
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
function SubAdminRow({ user: u, permList, basePath, resetBase, onChanged, scope }) {
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [perms, setPerms] = useState(u.permissions || []);

  const toggleActive = async () => {
    setBusy(true);
    try {
      await api.patch(`${basePath}/${u.id}`, { is_active: !u.is_active });
      toast.success(`${u.name} ${u.is_active ? "deactivated" : "activated"}`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`${basePath}/${u.id}`);
      toast.success(`${u.name} deleted`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const savePerms = async () => {
    setBusy(true);
    try {
      await api.patch(`${basePath}/${u.id}`, { permissions: perms });
      toast.success("Permissions updated");
      setEditing(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className={`rounded-3xl border-0 shadow-floating p-5 ${u.is_active === false ? "opacity-60" : ""}`} data-testid={`sub-row-${u.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-heading font-bold text-lg">{u.name}</p>
          <p className="text-sm text-slate-500">{u.email}</p>
          {u.must_change_password && <p className="text-xs text-[#92400E] mt-0.5">⚠️ Must change password on next login</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} className="rounded-full h-8 px-3 text-xs" data-testid={`edit-perms-${u.id}`}>
            {editing ? "Cancel" : "Permissions"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} className="rounded-full h-8 px-3 text-xs" data-testid={`reset-pw-${u.id}`}>
            <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
          </Button>
          <Button variant="outline" size="sm" onClick={toggleActive} disabled={busy}
            className={`rounded-full h-8 px-3 text-xs ${u.is_active === false ? "border-[#BBF7D0] text-[#15803D]" : "border-[#FED7AA] text-[#9A3412]"}`}
            data-testid={`toggle-${u.id}`}>
            {u.is_active === false ? <><Power className="w-3.5 h-3.5 mr-1" /> Activate</> : <><PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate</>}
          </Button>
          <Button variant="outline" size="sm" onClick={del} disabled={busy}
            className="rounded-full h-8 px-3 text-xs border-[#FECACA] text-[#B91C1C]" data-testid={`delete-${u.id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
        </div>
      </div>
      {editing && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <PermissionGrid value={perms} onChange={setPerms} permList={permList} />
          <Button onClick={savePerms} disabled={busy}
            className="mt-3 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-9 px-4"
            data-testid={`save-perms-${u.id}`}>
            {busy ? "Saving…" : "Save permissions"}
          </Button>
        </div>
      )}
      {!editing && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(u.permissions || []).length === 0 && <span className="text-xs text-slate-400">No permissions assigned</span>}
          {(u.permissions || []).map((p) => (
            <span key={p} className="inline-block text-[11px] bg-[#DBEAFE] text-[#1E40AF] px-2 py-0.5 rounded-full font-semibold">
              {p.replaceAll("_", " ")}
            </span>
          ))}
        </div>
      )}
      <ResetPasswordDialog open={resetOpen} onOpenChange={setResetOpen} user={u} endpoint={`${resetBase}/${u.id}/reset-password`} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
function PermissionGrid({ value, onChange, permList }) {
  const toggle = (p) => onChange(value.includes(p) ? value.filter((x) => x !== p) : [...value, p]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {permList.map((p) => (
        <label key={p} className="flex items-center gap-2 cursor-pointer text-sm">
          <input type="checkbox" checked={value.includes(p)} onChange={() => toggle(p)}
            className="w-4 h-4 rounded" data-testid={`perm-${p}`} />
          <span>{p.replaceAll("_", " ")}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
function CreateSubAdminDialog({ open, onOpenChange, permList, basePath, scope, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [perms, setPerms] = useState([]);
  const [busy, setBusy] = useState(false);

  const reset = () => { setName(""); setEmail(""); setPassword(""); setPerms([]); };
  const close = () => { reset(); onOpenChange(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(basePath, { name, email, password, permissions: perms });
      toast.success(`${scope === "super" ? "Sub-admin" : "Sub-owner"} created. They'll be asked to change the password on first login.`);
      close();
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="sm:max-w-lg rounded-3xl border-0 shadow-floating max-h-[90vh] overflow-y-auto" data-testid="create-sub-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold">
            Add a {scope === "super" ? "sub-super-admin" : "sub-owner"}
          </DialogTitle>
          <DialogDescription>
            Pick a name, email and temporary password. They'll be forced to change it on first login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-sub-form">
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-2xl h-11" data-testid="sub-name" /></Field>
          <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-2xl h-11" data-testid="sub-email" /></Field>
          <Field label="Temporary password (min 8 chars)"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="rounded-2xl h-11" data-testid="sub-password" /></Field>
          <div>
            <Label className="font-heading text-xs uppercase tracking-wider text-slate-500">Permissions</Label>
            <div className="mt-2 max-h-48 overflow-y-auto pr-2">
              <PermissionGrid value={perms} onChange={setPerms} permList={permList} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={close} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="sub-create-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : <><Check className="w-4 h-4 mr-1.5" /> Create</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="font-heading text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
function ResetPasswordDialog({ open, onOpenChange, user, endpoint }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(endpoint, { new_password: pw });
      toast.success(`Password reset. ${user.name} must change it on next login.`);
      setPw(""); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 shadow-floating" data-testid={`reset-dialog-${user?.id}`}>
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[#FEF3C7] flex items-center justify-center mb-2">
            <AlertTriangle className="w-6 h-6 text-[#92400E]" />
          </div>
          <DialogTitle className="text-center font-heading text-lg font-bold">Reset password for {user?.name}</DialogTitle>
          <DialogDescription className="text-center">They'll be forced to change it on next login.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="New temporary password">
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} className="rounded-2xl h-11" data-testid="reset-new-pw" />
          </Field>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={busy || pw.length < 8} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="reset-submit">
              {busy ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
